import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Target, Save } from "lucide-react";

const BASE_LINES = [["4300", "Εκδηλώσεις"], ["4200", "F&B"], ["4500", "Tesla"], ["4600", "Spa"], ["4700", "Transfers"], ["4400", "Λοιπά extras"]];
const MONTHS = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const MK = MONTHS.map((_, i) => String(i + 1));
const sum = (o) => MK.reduce((s, m) => s + (Number(o?.[m]) || 0), 0);

const Row = ({ code, label, row, draft, setDraft, canEdit }) => {
  const b = draft[code] || {};
  const a = row?.actual_months || {};
  const diff = sum(a) - sum(b);
  return (
    <>
      <tr className="border-t border-[#E3DED4]" data-testid={`inc-target-budget-${code}`}>
        <td rowSpan={2} className="px-3 py-1.5 font-medium text-[#45423C] whitespace-nowrap align-top">{label}</td>
        <td className="px-2 text-[10px] uppercase text-[#6F6A60]">Π/Υ</td>
        {MK.map((m) => (
          <td key={m} className="px-1 py-1">
            {canEdit ? (
              <input data-testid={`inc-target-input-${code}-${m}`} type="number" value={b[m] ?? 0}
                onChange={(e) => setDraft({ ...draft, [code]: { ...b, [m]: e.target.value } })}
                className="w-20 rounded border border-[#E3DED4] px-1.5 py-1 text-right text-xs tabular outline-none focus:border-[#45423C]" />
            ) : <span className="tabular text-xs">{fmtEuro(Number(b[m]) || 0)}</span>}
          </td>
        ))}
        <td className="px-2 text-right tabular font-semibold">{fmtEuro(sum(b))}</td>
        <td rowSpan={2} data-testid={`inc-target-diff-${code}`}
          className={`px-2 text-right tabular font-semibold align-middle ${diff < 0 ? "text-[#B5452F]" : "text-[#6E7A63]"}`}>{fmtEuro(diff)}</td>
      </tr>
      <tr className="bg-[#FAF8F4]" data-testid={`inc-target-actual-${code}`}>
        <td className="px-2 text-[10px] uppercase text-[#6F6A60]">Πραγμ.</td>
        {MK.map((m) => <td key={m} className="px-1 py-1 text-right tabular text-xs">{fmtEuro(Number(a[m]) || 0)}</td>)}
        <td className="px-2 text-right tabular font-semibold">{fmtEuro(sum(a))}</td>
      </tr>
    </>
  );
};

export const IncomeTargets = ({ year, canEdit }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["income-budget", year], queryFn: async () => (await api.get(`/finance/income-budget?year=${year}`)).data });
  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  const custom = new Set(meta?.custom_codes || []);
  const LINES = [...BASE_LINES, ...(meta?.accounts || []).filter((a) => a.section === "revenue" && custom.has(a.code)).map((a) => [a.code, a.name])];
  const [draft, setDraft] = useState({});
  const byCode = Object.fromEntries((data?.rows || []).map((r) => [r.account_code, r]));
  useEffect(() => {
    setDraft(Object.fromEntries((data?.rows || []).map((r) => [r.account_code, r.budget_months])));
  }, [data]);
  const save = useMutation({
    mutationFn: async () => {
      for (const [code] of LINES) {
        await api.put(`/finance/income-budget/line?year=${year}`, { account_code: code, months: Object.fromEntries(MK.map((m) => [m, Number(draft[code]?.[m]) || 0])) });
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["income-budget"] }); toast.success("Ο προϋπολογισμός αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="inc-targets-panel">
      <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-medium text-[#45423C] flex items-center gap-2"><Target className="h-4 w-4 text-[#C86235]" /> Εκδηλώσεις, F&B & extras — Προϋπολογισμός vs Πραγματικά</h2>
          <p className="text-sm text-[#6F6A60] mt-1">Καθαρά ποσά χωρίς ΦΠΑ. Εκδηλώσεις: από τη σελίδα Εκδηλώσεων ανά μήνα εκδήλωσης · υπόλοιπα: από τα παραστατικά. Ίδιος προϋπολογισμός με τον «Προϋπολογισμό Εσόδων».</p>
        </div>
        {canEdit && (
          <button data-testid="inc-targets-save" onClick={() => save.mutate()} disabled={save.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2F2D29] disabled:opacity-50">
            <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr><th className="px-3 py-2 text-left">Γραμμή</th><th />{MONTHS.map((m) => <th key={m} className="px-1 py-2 text-right">{m}</th>)}<th className="px-2 text-right">Σύνολο</th><th className="px-2 text-right">Διαφορά</th></tr>
          </thead>
          <tbody>
            {LINES.map(([code, label]) => <Row key={code} code={code} label={label} row={byCode[code]} draft={draft} setDraft={setDraft} canEdit={canEdit} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};
